"use client";

import { useEffect } from "react";

/** How far ahead of the viewport lazy images start, in viewport heights. */
export const IMAGE_WARMUP_MARGIN = { ahead: 1.5, aheadFast: 2.25, behind: 0.5 };

// Navigation previews, closed dialogs and hidden panels stay lazy.
const SKIP = 'nav, [aria-hidden="true"], [inert], [hidden], dialog:not([open]), [data-cc-image-warmup="off"]';

type NetworkInformationLike = { saveData?: boolean; effectiveType?: string; downlink?: number };

/** Margin for the observer, or null when the visitor asked to save data. */
export function imageWarmupRootMargin(connection?: NetworkInformationLike): string | null {
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? "")) return null;
  const fast = connection?.effectiveType === "4g" && (connection.downlink ?? 0) >= 5;
  const ahead = fast ? IMAGE_WARMUP_MARGIN.aheadFast : IMAGE_WARMUP_MARGIN.ahead;
  return `${IMAGE_WARMUP_MARGIN.behind * 100}% 0px ${ahead * 100}% 0px`;
}

/** Warm the browser-selected picture candidate without changing hydration-owned DOM. */
export function warmSelectedImage(img: HTMLImageElement): HTMLImageElement | undefined {
  if (!img.currentSrc) return;
  const preload = new Image();
  preload.decoding = "async";
  preload.onload = () => void preload.decode?.().catch(() => {});
  preload.src = img.currentSrc;
  return preload;
}

/**
 * Starts lazy images a little further ahead than the browser would, so they
 * are downloaded and decoded before the visitor scrolls to them. Chrome's own
 * lazy threshold is about 1250px, Safari and Firefox load much closer to the
 * viewport. Only images about to be seen are warmed (never the whole page);
 * it starts after the load event so it cannot compete with the LCP image, and
 * it does nothing for Save-Data or 2G visitors.
 */
export function CcImageWarmup() {
  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    const rootMargin = imageWarmupRootMargin(connection);
    if (!rootMargin || typeof IntersectionObserver === "undefined") return;

    let observer: IntersectionObserver | undefined;
    let mutations: MutationObserver | undefined;
    let cancelIdle: (() => void) | undefined;
    const warmed = new WeakSet<HTMLImageElement>();

    // `complete` alone is also true for a lazy image the browser has not fetched yet.
    const loaded = (img: HTMLImageElement) => img.complete && img.naturalWidth > 0;
    const promote = (img: HTMLImageElement) => {
      observer?.unobserve(img);
      if (loaded(img) || img.loading !== "lazy") return;
      // Sibling streamed boundaries may still be hydrating after this effect.
      // Never mutate their attributes; share the selected candidate's browser cache.
      if (warmSelectedImage(img)) warmed.add(img);
    };
    const watch = (img: HTMLImageElement) => {
      if (img.loading === "lazy" && !loaded(img) && !warmed.has(img) && !img.closest(SKIP)) observer?.observe(img);
    };
    const start = () => {
      observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && promote(entry.target as HTMLImageElement)),
        { rootMargin },
      );
      document.querySelectorAll<HTMLImageElement>('img[loading="lazy"]').forEach(watch);
      mutations = new MutationObserver((records) => {
        for (const record of records) {
          record.addedNodes.forEach((node) => {
            if (node instanceof HTMLImageElement) watch(node);
            else if (node instanceof Element) node.querySelectorAll<HTMLImageElement>('img[loading="lazy"]').forEach(watch);
          });
        }
      });
      mutations.observe(document.body, { childList: true, subtree: true });
    };
    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        const id = window.requestIdleCallback(start, { timeout: 1500 });
        cancelIdle = () => window.cancelIdleCallback(id);
      } else {
        const id = window.setTimeout(start, 200);
        cancelIdle = () => window.clearTimeout(id);
      }
    };

    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      window.removeEventListener("load", schedule);
      cancelIdle?.();
      observer?.disconnect();
      mutations?.disconnect();
    };
  }, []);

  return null;
}
